import {
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ContractStatus, GAS_TYPE_LABELS, GasType, UserRole } from '@bhops/shared';
import { infrastructureOrdersApi, type InfrastructureOrderDto } from '@/api/infrastructure-orders';
import { contractsApi } from '@/api/contracts';
import { extractApiError } from '@/api/client';
import { Button, Select } from '@/components/Form';
import { Modal } from '@/components/Modal';
import { MapUrlIcon } from '@/components/MapUrlIcon';
import { Icon } from '@/components/Icon';
import { useAuthStore } from '@/stores/auth';
import { useToast } from '@/components/Toast';
// 추가수정 2차(26-07-27) 정정 — 표시 날짜는 KST 정본으로.
import { kstTodayYmd, kstYmd, kstYmdHyphen } from '@/lib/format-date';
import {
  nextColumnSort,
  sortRowsByColumn,
  type ColumnSortKind,
  type ColumnSortState,
  type ColumnSortValue,
} from '@/lib/column-sort';
import { SortableTh } from '@/components/SortableTh';
import { useMediaQuery } from '@/lib/use-media-query';
import { InfrastructureOrderModal } from './infrastructure/InfrastructureOrderModal';
import { IntegratedCalendar } from '@/components/IntegratedCalendar';

// Plan §C-3 + §E-3-3 (PDF p.20) — 필드 그룹의 인프라 발주 페이지.
// S3 Phase 3.2 (spec §3.2) — 2-column 재편: 좌측 = 발주 roster(정보 카드),
// 우측 = 월간 설치 캘린더(InstallCalendarPage 흡수, 기존 설치 일정 탭 제거).
//
// 좌측 roster 는 installScheduledAt 오름차순 정렬. 각 행은 사업팀이 계약
// 완료 시 자동 기입(S1 Phase 11)한 7필드(고객사/위치 URL/담당자/직함/연락처/
// 가스 타입/희망 설치 일정)를 표시하고, 5단계 진행을 ✓ / — pill 로 노출하며,
// 클릭 시 공유 InfrastructureOrderModal 을 연다. 모달이 upsert + WD+5 검증을
// 담당하고 이 페이지는 순수 목록 + 캘린더.
//
// 자동 기입 표시 우선순위: InfraOrder snapshot(customerContact* / customerMapUrl)
//   > 라이브 customer 관계. snapshot 은 발주 시점 값 보존(이후 customer 편집과
//   무관) 이므로 먼저 사용하고, 구(舊) 데이터로 snapshot 이 비어 있으면 라이브
//   관계로 graceful fallback.
//
// Filters (기존 유지):
//   - 가스 종류 (LPG / LNG / 미정 / 전체)
//   - "미완료만" toggle (default on so the operator's working set is short)

// 이름표 정본은 `@bhops/shared` 다 — 슬랙 알림 카드도 같은 것을 쓴다.
// 여기 따로 적어 두면 화면과 알림이 갈린다(2026-09-16에 실제로 갈렸다).
const GAS_LABEL: Record<GasType, string> = GAS_TYPE_LABELS;

interface RowSelection {
  contractId: string;
  contractHumanId: string;
  initial: InfrastructureOrderDto | null;
}

// 2026-10-19 오너 요청 — "카테고리 명(고객사 위치 담당자 등) 클릭하면 정렬 가능하게". 고객사 마스터 ·
// 로봇 목록과 같은 정본(lib/column-sort): 글자는 가나다 → 역순 → 해제, 날짜는 최신 → 과거 → 해제,
// 설치 예정은 임박순(D-day 와 같은 규칙), 위치는 지도 있음 먼저, 가스는 LPG · LNG · 미정 순.
type InfraSortKey =
  | 'customer'
  | 'map'
  | 'contact'
  | 'position'
  | 'phone'
  | 'gas'
  | 'contacted'
  | 'ordered'
  | 'delivered'
  | 'install'
  | 'done';

const daysFromToday = (iso: string | null | undefined): number | null => {
  const ymd = iso ? kstYmdHyphen(iso) : null;
  if (!ymd) return null;
  return Math.round(
    (Date.parse(`${ymd}T00:00:00Z`) - Date.parse(`${kstTodayYmd()}T00:00:00Z`)) / 86_400_000,
  );
};
const time = (iso: string | null | undefined) => (iso ? Date.parse(iso) : null);
const GAS_ORDER: Record<string, number> = { LPG: 0, LNG: 1, UNDECIDED: 2 };

const INFRA_SORTS: Record<
  InfraSortKey,
  { kind: ColumnSortKind; value: (r: InfrastructureOrderDto) => ColumnSortValue }
> = {
  customer: { kind: 'text', value: (r) => r.customer?.displayName },
  map: {
    kind: 'flag',
    value: (r) => ((r.customerMapUrl ?? r.customer?.addressMapUrl) ? 0 : 1),
  },
  contact: {
    kind: 'text',
    value: (r) => r.customerContactName ?? r.customer?.primaryContactName,
  },
  position: {
    kind: 'text',
    value: (r) => r.customerContactPosition ?? r.customer?.primaryContactPosition,
  },
  phone: {
    kind: 'text',
    value: (r) => r.customerContactPhone ?? r.customer?.primaryContactPhone,
  },
  gas: { kind: 'category', value: (r) => (r.gasType ? (GAS_ORDER[r.gasType] ?? 3) : null) },
  contacted: { kind: 'date', value: (r) => time(r.contactedAt) },
  ordered: { kind: 'date', value: (r) => time(r.grillOrderedAt) },
  delivered: { kind: 'date', value: (r) => time(r.grillDeliveredAt) },
  install: { kind: 'imminent', value: (r) => daysFromToday(r.installScheduledAt) },
  done: { kind: 'date', value: (r) => time(r.installCompletedAt) },
};

export function InfrastructurePage() {
  // pdf-feedback-followups — CustomerDetailPage "더 보기 →"
  // (/infrastructure?customerId=…) 와 인프라 알림 딥링크가 customer 로 좁혀
  // 들어온다. 이전엔 이 파라미터를 무시해 전체 roster 가 떠 사용자가 다시
  // 찾아야 했다. 파라미터가 있으면 list 쿼리에 전달하고 배너+전체보기 제공.
  const [searchParams, setSearchParams] = useSearchParams();
  const customerId = searchParams.get('customerId') || undefined;

  const [gasFilter, setGasFilter] = useState<'' | GasType>('');
  // customer 딥링크로 진입하면 그 고객사의 발주를 모두(완료 포함) 보여주는 게
  // 자연스러우므로 기본 '미완료만' 을 끈다. 일반 진입은 종전대로 미완료만 ON.
  const [pendingOnly, setPendingOnly] = useState(!customerId);
  const [selected, setSelected] = useState<RowSelection | null>(null);
  // Plan v0.3.0 §I-1 — "+ 신규 발주" picker. When set, the user has
  // clicked the new-order button and is choosing which active
  // contract to attach the order to.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [colSort, setColSort] = useState<ColumnSortState<InfraSortKey> | null>(null);

  // FB §6 (결정 D) — 좁은 폭(≤960px)에서는 roster/캘린더를 1열로 stack.
  // 그 이상에서는 비대칭 2열(좌 roster 크게 1.7fr / 우 캘린더 작게 1fr).
  const narrow = useMediaQuery('(max-width: 960px)');
  // 표 ↔ 캘린더 폭 끌어 조절(2026-09-30 오너 요청). 왼쪽(표) 비율 %, 이 PC 에 기억한다.
  const split = useSplitPercent();

  const role = useAuthStore((s) => s.user?.role);
  // data-lifecycle §3/D3 (PR-5c) — 인프라 발주 삭제/복구/완전삭제는 필드 쓰기권한
  // (백엔드 canWriteField=ADMIN/FIELD_MANAGER/MANAGER)과 일치.
  const canWriteField =
    role === UserRole.ADMIN || role === UserRole.FIELD_MANAGER || role === UserRole.MANAGER;
  // 삭제됨 포함 토글.
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const toast = useToast();
  const qc = useQueryClient();
  const removeMx = useMutation({
    mutationFn: (id: string) => infrastructureOrdersApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['infrastructure-orders'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('인프라 발주를 삭제했습니다. 나중에 복구할 수 있습니다.', '인프라 발주');
    },
    onError: (err) => toast.error(extractApiError(err), '삭제 실패'),
  });
  const restoreMx = useMutation({
    mutationFn: (id: string) => infrastructureOrdersApi.restore(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['infrastructure-orders'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('인프라 발주를 복구했습니다.', '인프라 발주');
    },
    onError: (err) => toast.error(extractApiError(err), '복구 실패'),
  });
  const hardDeleteMx = useMutation({
    mutationFn: (id: string) => infrastructureOrdersApi.hardDelete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['infrastructure-orders'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('인프라 발주를 완전히 삭제했습니다.', '인프라 발주');
    },
    onError: (err) => toast.error(extractApiError(err), '완전 삭제 실패'),
  });
  // §I-1: button visible only to admin / *_MANAGER. Other roles still
  // edit existing orders via the row-level 편집 button.
  const canCreate =
    role === UserRole.ADMIN ||
    role === UserRole.MANAGER ||
    role === UserRole.SALES_MANAGER ||
    role === UserRole.MFG_MANAGER ||
    role === UserRole.FIELD_MANAGER;

  const listQ = useQuery({
    queryKey: [
      'infrastructure-orders',
      {
        gasType: gasFilter || null,
        pendingOnly,
        customerId: customerId || null,
        includeDeleted,
      },
    ],
    queryFn: () =>
      infrastructureOrdersApi.list({
        gasType: gasFilter || undefined,
        pendingOnly,
        customerId,
        ...(includeDeleted ? { includeDeleted: true } : {}),
      }),
    staleTime: 30_000,
  });

  // 배너에 표시할 고객사명 — 결과 행의 customer 스냅샷에서 도출(없으면 generic).
  const filteredCustomerName = customerId ? (listQ.data?.[0]?.customer?.displayName ?? null) : null;

  const stats = useMemo(() => {
    const rows = listQ.data ?? [];
    const total = rows.length;
    const contacted = rows.filter((r) => !!r.contactedAt).length;
    const ordered = rows.filter((r) => !!r.grillOrderedAt).length;
    const delivered = rows.filter((r) => !!r.grillDeliveredAt).length;
    const installed = rows.filter((r) => !!r.installCompletedAt).length;
    return { total, contacted, ordered, delivered, installed };
  }, [listQ.data]);

  const sortedRows = useMemo(() => {
    const rows = listQ.data ?? [];
    if (!colSort) return rows;
    const spec = INFRA_SORTS[colSort.key];
    return sortRowsByColumn(rows, spec.kind, spec.value, colSort.step);
  }, [listQ.data, colSort]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div>
          <h1 className="kuun-h2" style={{ margin: 0 }}>
            캘린더 · 인프라 발주
          </h1>
        </div>
        {canCreate && (
          // Plan v0.3.0 §I-1 — direct entry point so operators can
          // start a new order without first navigating to a contract
          // detail page. Click → ContractPickerModal → existing
          // InfrastructureOrderModal in create mode.
          <Button type="button" variant="primary" onClick={() => setPickerOpen(true)}>
            + 신규 발주
          </Button>
        )}
      </header>

      {customerId && (
        <div style={filterBanner}>
          <span>
            <strong>{filteredCustomerName ?? '선택한 고객사'}</strong>의 인프라 발주만 보고 있습니다
          </span>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              // customerId 파라미터만 제거(다른 파라미터는 보존). 전체 roster 복귀.
              const next = new URLSearchParams(searchParams);
              next.delete('customerId');
              setSearchParams(next, { replace: true });
            }}
          >
            전체 보기
          </Button>
        </div>
      )}

      <section
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 12,
        }}
      >
        <Stat label="총 발주" value={stats.total} />
        <Stat label="연락 완료" value={stats.contacted} />
        <Stat label="발주 완료" value={stats.ordered} />
        <Stat label="납품 완료" value={stats.delivered} />
        <Stat label="설치 완료" value={stats.installed} />
      </section>

      {/*
       * S3 Phase 3.2 (spec §3.2) — 2-column 레이아웃.
       *   좌측: 발주 roster (정보 카드 = 첨부 스크린샷의 발주 테이블 자체)
       *   우측: 월간 캘린더 (IntegratedCalendar — 발주/납품/설치/유지보수)
       * FB §6 (결정 D) — 대칭 50/50 → 비대칭(좌 1.7fr / 우 1fr) 으로 roster 확대.
       * 960px 이하에서는 1열로 stack(narrow). 캘린더가 roster 아래로 내려간다.
       */}
      <div
        ref={split.containerRef}
        style={{
          ...twoCol,
          ...(narrow
            ? { gridTemplateColumns: '1fr' }
            : {
                gap: 0,
                gridTemplateColumns: `minmax(0, ${split.pct}fr) ${SPLIT_HANDLE_W}px minmax(320px, ${100 - split.pct}fr)`,
              }),
        }}
      >
        <section style={card}>
          <div style={toolbarRow}>
            <label style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
              가스 종류:
              <Select
                value={gasFilter}
                onChange={(e) => setGasFilter(e.target.value as '' | GasType)}
                style={{ marginLeft: 6, width: 120 }}
              >
                <option value="">전체</option>
                <option value={GasType.LPG}>LPG</option>
                <option value={GasType.LNG}>LNG</option>
                <option value={GasType.UNDECIDED}>미정</option>
              </Select>
            </label>
            <label style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
              <input
                type="checkbox"
                checked={pendingOnly}
                onChange={(e) => setPendingOnly(e.target.checked)}
                style={{ marginRight: 6 }}
              />
              미완료만
            </label>
            {/* data-lifecycle §3 (PR-5c) — 삭제됨 포함(필드 쓰기권한 전용). 복구/완전삭제용. */}
            {canWriteField && (
              <label style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                <input
                  type="checkbox"
                  checked={includeDeleted}
                  onChange={(e) => setIncludeDeleted(e.target.checked)}
                  style={{ marginRight: 6 }}
                />
                삭제됨 포함
              </label>
            )}
          </div>

          {listQ.isError && <div style={errorBox}>{extractApiError(listQ.error)}</div>}

          {/*
           * 수정 사항2.pdf p.3 + S3 §3.2 + FB3 §11 — 컬럼 재구성.
           *   - 제거: 계약 (CNT-...) — 현장에서 활용도 낮음, 편집 modal 에서 여전히 사용.
           *   - 제거(FB3 §11): 매장 / 주소 — 위치는 고객사 마스터 지도 URL 로 충분.
           *   - 위치(지도 URL N/G 아이콘) / 담당자 / 직함 / 연락처 유지
           *           — 현장팀이 한 화면에서 고객에 바로 전화/방문 가능하도록.
           *   - 자동 기입 값: InfraOrder snapshot 우선, 없으면 라이브 customer.
           *   - 단계 pill (연락/발주/납품/완료) 4개는 그대로 유지.
           *   - 완료 컬럼(FB3 §11): 상태 표시(✓/—)+완료일만. 완료 처리/해제는
           *     편집 모달(InfrastructureOrderModal)로 이동.
           */}
          <div style={{ overflowX: 'auto' }}>
            <table style={tableBase}>
              <thead>
                <tr>
                  {(
                    [
                      ['customer', '고객사', false],
                      ['map', '위치', true],
                      ['contact', '담당자', false],
                      ['position', '직함', false],
                      ['phone', '연락처', false],
                      ['gas', '가스', true],
                      ['contacted', '연락', true],
                      ['ordered', '발주', true],
                      ['delivered', '납품', true],
                      ['install', '설치 예정', true],
                      ['done', '완료', true],
                    ] as const
                  ).map(([key, label, center]) => (
                    <SortableTh
                      key={key}
                      label={label}
                      kind={INFRA_SORTS[key].kind}
                      step={colSort?.key === key ? colSort.step : null}
                      onToggle={() =>
                        setColSort((cur) => nextColumnSort(cur, key, INFRA_SORTS[key].kind))
                      }
                      testId={`infra-sort-th-${key}`}
                      thStyle={center ? { ...th, textAlign: 'center' } : th}
                    />
                  ))}
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r) => {
                  // 자동 기입(snapshot) 우선, 비어 있으면 라이브 customer 관계로 fallback.
                  const contactName =
                    r.customerContactName ?? r.customer?.primaryContactName ?? null;
                  const contactPosition =
                    r.customerContactPosition ?? r.customer?.primaryContactPosition ?? null;
                  const contactPhone =
                    r.customerContactPhone ?? r.customer?.primaryContactPhone ?? null;
                  // windows-ux-edit Round 2 Phase D (#6) — 스냅샷 우선,
                  // 비면 라이브 customer.addressMapUrl 로 fallback.
                  const mapUrl = r.customerMapUrl ?? r.customer?.addressMapUrl ?? null;
                  return (
                    <tr
                      key={r.id}
                      style={{
                        borderTop: '1px solid var(--border-subtle)',
                        // data-lifecycle §3 — 삭제됨 row 는 흐리게.
                        opacity: r.deletedAt ? 0.55 : 1,
                      }}
                    >
                      {/* round5 #15b — 고객사명을 상세 페이지 링크로. customerId 는
                          required FK 라 항상 존재; 표시명 없으면 비링크 '-' 유지. */}
                      <td style={td}>
                        {r.customer?.displayName ? (
                          <Link to={`/customers/${r.customerId}`} style={customerLink}>
                            {r.customer.displayName}
                          </Link>
                        ) : (
                          '-'
                        )}
                        {r.deletedAt && (
                          <span
                            style={{
                              marginLeft: 6,
                              fontSize: 10,
                              fontWeight: 700,
                              color: 'var(--severity-urgent)',
                            }}
                          >
                            삭제됨
                          </span>
                        )}
                      </td>
                      <td style={tdCenter}>{mapUrl ? <MapUrlIcon url={mapUrl} /> : '-'}</td>
                      <td style={td}>{contactName ?? '-'}</td>
                      <td style={td}>{contactPosition ?? '-'}</td>
                      <td style={tdMono}>{contactPhone ?? '-'}</td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        {/* round3 #1 — gasType(enum) 없으면 사업팀 입력 grillSelectorType fallback. */}
                        {r.gasType ? GAS_LABEL[r.gasType] : (r.grillSelectorType ?? '-')}
                      </td>
                      <td style={tdCenter}>
                        <Pill done={!!r.contactedAt} label="연락" />
                      </td>
                      <td style={tdCenter}>
                        <Pill done={!!r.grillOrderedAt} label="발주" />
                      </td>
                      <td style={tdCenter}>
                        <Pill done={!!r.grillDeliveredAt} label="납품" />
                      </td>
                      <td style={{ ...tdMono, textAlign: 'center' }}>
                        {/* docs/work_log v1209 — Date 객체 leak 방지.
                            추가수정 2차(26-07-27) 정정 — 매장 상세(StoreDetailPage)가
                            KST 로 바뀌었는데 여기만 UTC 자르기라 같은 발주가 두 화면에서
                            다른 날짜로 보였다. 표기(점 구분)까지 매장 상세와 맞춘다. */}
                        {kstYmd(r.installScheduledAt) ?? '-'}
                      </td>
                      {/*
                       * FB3 §11 — 완료 셀: 상태 배지 + 완료일만 표시.
                       *   완료 처리/해제 액션은 편집 모달(InfrastructureOrderModal)로
                       *   이동 — 테이블 행은 상태 표시 전용.
                       * 자동 완료(Acceptance finalize) 로 채워진 행도 동일하게 표시.
                       */}
                      <td style={tdCenter}>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          <Pill done={!!r.installCompletedAt} label="완료" />
                          {r.installCompletedAt && (
                            <span
                              style={{
                                fontFamily: 'var(--font-mono)',
                                fontSize: 11,
                                color: 'var(--fg-muted)',
                              }}
                            >
                              {/* 추가수정 2차(26-07-27) 정정 — "설치 완료 처리" 버튼이
                                  ymdToIso(로컬 자정)로 저장하던 시절의 값이 한국 자정
                                  = 전날 15:00Z 라, UTC 자르기면 하루 이르게 보였다.
                                  버튼의 저장 형식은 모달에서 고쳤지만 기존 행이 그대로
                                  남아 있으므로 표시는 계속 KST 로 환산한다. */}
                              {kstYmd(r.installCompletedAt) ?? '-'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={td}>
                        <div style={{ display: 'inline-flex', gap: 6 }}>
                          {r.deletedAt
                            ? // data-lifecycle §3/D3 — 삭제됨: 복구 / 완전삭제(필드 쓰기권한).
                              canWriteField && (
                                <>
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    disabled={restoreMx.isPending}
                                    onClick={() => restoreMx.mutate(r.id)}
                                  >
                                    복구
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="danger"
                                    disabled={hardDeleteMx.isPending}
                                    onClick={() => {
                                      if (
                                        window.confirm(
                                          `"${r.customer?.displayName ?? r.contract?.humanId ?? ''}" 인프라 발주를 완전 삭제할까요?\n되돌릴 수 없습니다.`,
                                        )
                                      ) {
                                        hardDeleteMx.mutate(r.id);
                                      }
                                    }}
                                  >
                                    완전 삭제
                                  </Button>
                                </>
                              )
                            : // 활성: 편집 + 소프트삭제(필드 쓰기권한, 복구 가능).
                              [
                                <Button
                                  key="edit"
                                  type="button"
                                  variant="secondary"
                                  onClick={() =>
                                    setSelected({
                                      contractId: r.contractId,
                                      contractHumanId: r.contract?.humanId ?? r.contractId,
                                      initial: r,
                                    })
                                  }
                                >
                                  편집
                                </Button>,
                                canWriteField && (
                                  <Button
                                    key="del"
                                    type="button"
                                    variant="danger"
                                    disabled={removeMx.isPending}
                                    onClick={() => {
                                      if (
                                        window.confirm(
                                          `"${r.customer?.displayName ?? r.contract?.humanId ?? ''}" 인프라 발주를 삭제할까요?\n발주, 납품, 설치 일정이 캘린더에서 함께 사라집니다. 나중에 복구할 수 있습니다.`,
                                        )
                                      ) {
                                        removeMx.mutate(r.id);
                                      }
                                    }}
                                  >
                                    삭제
                                  </Button>
                                ),
                              ]}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!listQ.isLoading && (listQ.data ?? []).length === 0 && (
                  <tr>
                    <td colSpan={12} style={empty}>
                      {pendingOnly ? '미완료 인프라 발주가 없습니다.' : '인프라 발주가 없습니다.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {!narrow && (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="표와 캘린더 폭 조절"
            aria-valuenow={Math.round(split.pct)}
            title="끌어서 표와 캘린더 폭을 바꿉니다. 두 번 누르면 처음 비율로 돌아갑니다."
            data-testid="infra-split-handle"
            onMouseDown={split.begin}
            onDoubleClick={split.reset}
            style={{
              alignSelf: 'stretch',
              cursor: 'col-resize',
              display: 'flex',
              justifyContent: 'center',
              userSelect: 'none',
            }}
          >
            <div
              style={{
                width: split.dragging ? 4 : 2,
                margin: '8px 0',
                borderRadius: 2,
                background: split.dragging ? 'var(--fg-brand)' : 'var(--border-default)',
              }}
            />
          </div>
        )}

        {/*
         * FB §6 (결정 D) — 우측 캘린더를 InstallCalendarPage(설치만) 에서
         * IntegratedCalendar 로 교체. 발주(📐)/납품(📦)/설치(🚚)/유지보수(🛠️)
         * 토글을 한 캘린더에서 제공한다. 인프라 발주 페이지 맥락에 맞춰
         * 발주·납품·설치 기본 ON(유지보수만 OFF). 토글 선호는 페이지 전용
         * storageKey 로 독립 영속 — 대시보드 캘린더 선호와 섞이지 않는다.
         * IntegratedCalendar 가 자체 카드/헤더를 렌더하므로 외부 card 래퍼는
         * 두지 않는다(중첩 카드 방지). infra 발주는 list({installFrom,installTo})
         * 로 읽으므로 이 페이지에서도 standalone 동작.
         */}
        <div style={{ minWidth: 0 }}>
          <IntegratedCalendar
            defaultActive={{
              install: true,
              order: true,
              delivery: true,
              maintenance: false,
              return: true,
              // 2026-10-20 — 로봇 교체도 설치(+ 회수)라 이 화면에서 켠다.
              replace: true,
            }}
            storageKey="kuun:integrated-calendar:infrastructure"
          />
        </div>
      </div>

      {selected && (
        <InfrastructureOrderModal
          open
          onClose={() => setSelected(null)}
          contractId={selected.contractId}
          contractHumanId={selected.contractHumanId}
          initial={selected.initial}
        />
      )}

      {pickerOpen && (
        <ContractPickerModal
          open
          onClose={() => setPickerOpen(false)}
          onPick={(c) => {
            setPickerOpen(false);
            // Open the existing order modal in create mode (initial=null)
            // attached to the chosen contract. Save mutation invalidates
            // the order list + dashboard KPIs (§I-4 — handled inside
            // InfrastructureOrderModal's onSuccess).
            setSelected({
              contractId: c.id,
              contractHumanId: c.humanId,
              initial: null,
            });
          }}
        />
      )}
    </div>
  );
}

// ============================================================
// ContractPickerModal — Plan v0.3.0 §I-2
// ============================================================
//
// Active-contracts picker shown when the operator clicks "+ 신규 발주".
// Lists status=ACTIVE contracts with a search input. Picking one
// closes this modal and opens the existing InfrastructureOrderModal
// in create mode pre-attached to that contract.
//
// Note: §I-3 (allowing orphan orders without a contract) is deferred
// to v0.4.0 because it requires nullable `contractId` at the schema
// level. Until then, every infrastructure order needs a contract,
// so this picker is the canonical entry point.

function ContractPickerModal({
  open,
  onClose,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  onPick: (c: { id: string; humanId: string }) => void;
}) {
  const [search, setSearch] = useState('');
  // 기본은 ACTIVE 만 노출 — `ContractsService.sign` (contracts.service.ts:308)
  // 이 status: ACTIVE + signedAt 을 atomic 하게 set 하므로 "SIGNED 했는데
  // ACTIVE 가 아닌 상태" 는 schema 상 존재하지 않음. 인프라 발주가 가능한
  // 시점 = 계약 사인 완료 시점 = ACTIVE 시점. 따라서 audit 에서 우려했던
  // "사인 직후 picker 가 비어 있음" 시나리오는 실제로 발생하지 않음.
  //
  // 사인 전 (DRAFT) 단계에서 advance infra 발주 정책이 필요하면 별도
  // PR — 현재 backend 도 contract FK 가 required 이므로 그때 DRAFT 도
  // 노출 토글이 필요할 수 있음 (현 시점은 보수적으로 ACTIVE 만).
  const listQ = useQuery({
    queryKey: ['contracts', 'picker', { status: ContractStatus.ACTIVE, search }],
    queryFn: () =>
      contractsApi.list({
        status: ContractStatus.ACTIVE,
        search: search.trim() || undefined,
        pageSize: 50,
      }),
    staleTime: 30_000,
  });

  return (
    <Modal open={open} onClose={onClose} title="신규 발주할 계약 선택" width={520}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <input
          type="search"
          placeholder="계약번호, 고객사, 매장 이름으로 검색"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            padding: '8px 10px',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--r-input)',
            fontSize: 13,
          }}
        />
        {listQ.isError && <div style={errorBox}>{extractApiError(listQ.error)}</div>}
        <div
          style={{
            maxHeight: 360,
            overflow: 'auto',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--r-card)',
          }}
        >
          {listQ.isLoading && (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--fg-muted)' }}>불러오는 중…</div>
          )}
          {!listQ.isLoading &&
            (listQ.data?.items ?? []).map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => onPick({ id: c.id, humanId: c.humanId })}
                style={pickerRow}
              >
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{c.humanId}</span>
                <span style={{ flex: 1, marginLeft: 12, fontSize: 13 }}>
                  {c.customer?.displayName ?? '-'}
                  {c.store && (
                    <span style={{ color: 'var(--fg-muted)', marginLeft: 6 }}>
                      · {[c.store.name, c.store.branchName].filter(Boolean).join(' ')}
                    </span>
                  )}
                </span>
              </button>
            ))}
          {!listQ.isLoading && (listQ.data?.items ?? []).length === 0 && (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--fg-muted)' }}>
              {search ? '검색 결과가 없습니다.' : '활성 계약이 없습니다.'}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

const pickerRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: '8px 12px',
  border: 'none',
  borderBottom: '1px solid var(--border-subtle)',
  background: 'transparent',
  cursor: 'pointer',
  textAlign: 'left',
};

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div style={statCard}>
      <div className="kuun-label" style={{ color: 'var(--fg-muted)' }}>
        {label}
      </div>
      <div className="kuun-kpi" style={{ marginTop: 4 }}>
        {value.toLocaleString('ko-KR')}
      </div>
    </div>
  );
}

function Pill({ done, label }: { done: boolean; label: string }) {
  return (
    <span
      aria-label={`${label} ${done ? '완료' : '미완료'}`}
      style={{
        display: 'inline-block',
        minWidth: 24,
        padding: '2px 8px',
        borderRadius: 'var(--r-pill)',
        fontSize: 11,
        fontWeight: 600,
        background: done ? 'var(--green-50)' : 'var(--bg-sunken)',
        color: done ? 'var(--green-700)' : 'var(--fg-muted)',
        border: done ? '1px solid var(--green-300)' : '1px solid var(--border-default)',
      }}
    >
      {/* 표의 예/아니오 통일 규약 — check = '예', em dash = '해당 없음'.
          docs/design/icon-vocabulary.md 참조. */}
      {done ? <Icon name="check" size={14} aria-hidden /> : '—'}
    </span>
  );
}

// ── 표 ↔ 캘린더 폭 조절(2026-09-30) ─────────────────────────────────────────
const SPLIT_HANDLE_W = 16;
const SPLIT_KEY = 'kuun:infrastructure:split-pct';
/** 처음 비율 — 종전 고정값 1.7fr : 1fr 과 같다. */
const SPLIT_DEFAULT = 63;
const SPLIT_MIN = 35;
const SPLIT_MAX = 80;

function readSplit(): number {
  try {
    const v = Number(window.localStorage.getItem(SPLIT_KEY));
    return v >= SPLIT_MIN && v <= SPLIT_MAX ? v : SPLIT_DEFAULT;
  } catch {
    return SPLIT_DEFAULT;
  }
}

function useSplitPercent() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pct, setPct] = useState(readSplit);
  const [dragging, setDragging] = useState(false);
  const pctRef = useRef(pct);
  pctRef.current = pct;
  const save = (v: number) => {
    try {
      window.localStorage.setItem(SPLIT_KEY, String(Math.round(v * 10) / 10));
    } catch {
      // 저장 못 해도(사생활 보호 창 등) 이번 화면에서는 그대로 쓴다.
    }
  };
  const begin = (e: ReactMouseEvent) => {
    const box = containerRef.current?.getBoundingClientRect();
    if (!box || e.button !== 0) return;
    e.preventDefault();
    setDragging(true);
    const onMove = (ev: MouseEvent) => {
      const v = ((ev.clientX - box.left) / box.width) * 100;
      setPct(Math.max(SPLIT_MIN, Math.min(SPLIT_MAX, v)));
    };
    const onUp = () => {
      setDragging(false);
      save(pctRef.current);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
    };
    document.body.style.cursor = 'col-resize';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };
  const reset = () => {
    setPct(SPLIT_DEFAULT);
    save(SPLIT_DEFAULT);
  };
  return { containerRef, pct, dragging, begin, reset };
}

// S3 §3.2 + FB §6 — 정보(roster) 좌 / 캘린더 우 2-column 베이스.
// gridTemplateColumns 는 useMediaQuery(narrow) 기반으로 렌더 시점에 주입:
//   - 넓은 폭: 'minmax(0, 1.7fr) minmax(320px, 1fr)' (비대칭, roster 확대)
//   - 좁은 폭(≤960px): '1fr' (1열 stack).
const twoCol: CSSProperties = {
  display: 'grid',
  gap: 16,
  alignItems: 'start',
};

const card: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
  padding: 16,
};

const statCard: CSSProperties = {
  ...card,
  border: '1px solid var(--border-card)',
  padding: 12,
};

const toolbarRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 18,
  flexWrap: 'wrap',
  marginBottom: 12,
};

const tableBase: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};

const th: CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  fontWeight: 600,
  color: 'var(--fg-muted)',
  background: 'var(--bg-sunken)',
  borderBottom: '1px solid var(--border-subtle)',
};

const td: CSSProperties = { padding: '8px 10px', verticalAlign: 'middle' };
const tdMono: CSSProperties = { ...td, fontFamily: 'var(--font-mono)' };
const tdCenter: CSSProperties = { ...td, textAlign: 'center' };
// round5 #15b — 고객사 상세 링크 (CustomerMasterPage 의 링크 톤과 동일).
const customerLink: CSSProperties = { color: 'var(--fg-brand)', textDecoration: 'none' };

const empty: CSSProperties = {
  padding: '24px 12px',
  textAlign: 'center',
  color: 'var(--fg-muted)',
  fontSize: 13,
};

const errorBox: CSSProperties = {
  padding: 10,
  marginBottom: 12,
  background: 'var(--bg-danger-subtle)',
  border: '1px solid var(--fg-danger)',
  color: 'var(--fg-danger)',
  borderRadius: 'var(--r-card)',
  fontSize: 12,
};

const filterBanner: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '10px 14px',
  background: 'var(--brand-subtle)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-md)',
  fontSize: 13,
  color: 'var(--fg-default)',
};
